<script setup lang="ts">
import { computed, ref } from 'vue';
import { useNodeStore } from 'gpcore-apikit/store/pinia/node';
import { create } from '@bufbuild/protobuf';
import { ServerProvisioningState } from 'gpcore-grpc/gpcore/api/cloud/v1/server_pb';
import { OperatingSystemFamily } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import { NetworkType } from 'gpcore-grpc/gpcore/api/cloud/v1/network_pb';
import { RescueModeSchema } from 'gpcore-grpc/gpcore/api/cloud/v1/node_pb';
import type { ServiceError } from 'gpcore-apikit/grpc/types';

import useProjectViewTitle from '@panel/composables/useProjectViewTitle';
import useNotifications from 'gpcore-uikit/composables/useNotifications';

import ProjectNodeLayout from '@panel/layouts/Layout.Project.Node.vue';
import Section from 'gpcore-uikit/components/Component.Section.vue';
import CommandText from 'gpcore-uikit/components/Component.Command.Text.vue';
import ExternalLink from 'gpcore-uikit/components/Component.ExternalLink.vue';
import PlatformManagementAccessModal from '@panel/components/node/PlatformManagementAccessModal.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { GpBtn, GpPasswordInput } from '@gportal/uikit';
import Size from 'gpcore-uikit/interfaces/Size';

useProjectViewTitle('Node Access');

const { addNotification } = useNotifications();
const nodeStore = useNodeStore();

const showConsoleDialog = ref(false);

const isRescueModeEnabled = computed(() =>
  [
    ServerProvisioningState.RESCUE,
    ServerProvisioningState.RESCUING,
    ServerProvisioningState.RESCUE_WAIT,
    ServerProvisioningState.RESCUE_FAIL,
  ].includes(nodeStore.node.status),
);

const rescuePassword = ref('');

const enterRescueMode = () => {
  const mode = create(RescueModeSchema);
  mode.enabled = true;
  mode.password = rescuePassword.value;

  nodeStore
    .toggleRescueMode(mode)
    .then(() => {
      addNotification({ message: 'The rescue mode has been enabled.', type: 'success' });
    })
    .catch((error: ServiceError) => {
      addNotification({ message: error.message, type: 'danger' });
    })
    .finally(() => {
      rescuePassword.value = '';
    });
};

const leaveRescueMode = () => {
  const mode = create(RescueModeSchema);
  mode.enabled = false;

  nodeStore
    .toggleRescueMode(mode)
    .then(() => {
      addNotification({ message: 'The rescue mode has been disabled.', type: 'success' });
    })
    .catch((error: ServiceError) => {
      addNotification({ message: error.message, type: 'danger' });
    });
};

const openPlatformManagement = () => {
  showConsoleDialog.value = true;
};
</script>

<template>
  <ProjectNodeLayout :content-size="Size.Tiny">
    <template #content>
      <Section
        v-if="nodeStore.node.loginHint !== undefined"
        :size="Size.Tiny"
        title="Authentication"
      >
        <template
          v-if="nodeStore.node.image.operatingSystem?.family === OperatingSystemFamily.WINDOWS"
        >
          <p>
            Please use Microsoft Remote Desktop (RDP) client to connect to your server. The username
            is
            <CommandText> {{ nodeStore.node.loginHint.username }} </CommandText>.
          </p>
        </template>

        <template v-else>
          <p>
            Please use a SSH client to connect to your server. The username is
            <CommandText>{{ nodeStore.node.loginHint.username }} </CommandText>. You can use the
            following command to connect to your server:

            <CommandText>
              ssh {{ nodeStore.node.loginHint.username }}@{{
                nodeStore.node.networkInterfaces
                  .filter((iface) => iface.type === NetworkType.PUBLIC)
                  .map((iface) => iface.ipAddresses)
                  .map((ip) => ip[0])[0]
              }} </CommandText
            >.
          </p>

          <br />

          <p v-if="nodeStore.node.loginHint.hasPassword">
            Use your selected password to authenticate. The password has been set during the
            creation of the node. If you have lost your password, you need to reinstall the node.
          </p>
          <p v-else>
            You have to use your private SSH key to authenticate. The list of allowed SSH keys has
            been set in the <CommandText>authorized_keys</CommandText> file, owned by user
            <CommandText>{{ nodeStore.node.loginHint.username }}</CommandText> during the creation
            of the node. If you have lost your private SSH key, you need to
            <ExternalLink url="https://www.man7.org/linux/man-pages/man1/ssh-keygen.1.html">
              generate a new key pair
            </ExternalLink>
            and <RouterLink :to="{ name: 'UserSSHKeys' }">add</RouterLink> the public part of the
            SSH key SSH key to your user and reinstall the node.
          </p>

          <br />

          <p>
            After logging into the node via SSH with the user
            <CommandText>{{ nodeStore.node.loginHint.username }}</CommandText
            >, you can use <CommandText>sudo -s</CommandText> to become root.
          </p>
        </template>
      </Section>

      <PlatformManagementAccessModal :show="showConsoleDialog" @close="showConsoleDialog = false" />
      <Section
        v-if="nodeStore.node.hasConsoleAccess"
        :size="Size.Tiny"
        title="Platform Management Access"
      >
        <p>
          You can access the platform management console of your node. This allows you to access the
          monitor and the keyboard directly, even if the node stuck while booting or has a broken
          network configuration.
        </p>

        <GpBtn
          class="tw-mt-8 tw-w-full"
          label="Access Platform Management"
          :icon="['fas', 'fa-terminal']"
          @click="openPlatformManagement"
        />
      </Section>

      <Section v-if="!isRescueModeEnabled" :size="Size.Tiny" title="Rescue Mode">
        <p class="tw-mb-4">
          If you are unable to boot your machine, you can boot the machine into the Rescue Mode. For
          further documentation on how to use the rescue mode, please refer to the
          <ExternalLink :url="$config.docs.url + '/guides/rescue-mode/'">documentation</ExternalLink
          >.
        </p>

        <h5 class="tw-mb-4">Authentication</h5>
        <p class="tw-mb-2">
          Please enter a SSH password, this password is needed to login via SSH to your server after
          booting from into the rescue mode. The password needs at least 5 characters.
        </p>

        <GpPasswordInput
          input-id="node-access-password"
          v-model="rescuePassword"
          placeholder="SSH password"
          enable-clear-text-view
          transparent
        />
        <GpBtn
          class="tw-mt-8 tw-w-full"
          btn-type="warning"
          :disabled="!rescuePassword || rescuePassword.length < 5 || !nodeStore.nodeActionsAllowed"
          @click="enterRescueMode()"
        >
          <FontAwesomeIcon icon="life-ring" /> Reboot into rescue mode
        </GpBtn>
      </Section>

      <Section v-if="isRescueModeEnabled" :size="Size.Tiny" title="Leave Rescue Mode">
        <p class="tw-mb-4">Your node is currently running in rescue mode.</p>

        <p>
          Please login with user <CommandText>rescue</CommandText>
          via SSH on your node using your chosen password.
        </p>
        <GpBtn
          class="tw-mt-8 tw-w-full"
          outline
          :disabled="!nodeStore.nodeActionsAllowed"
          @click="leaveRescueMode()"
        >
          Leave rescue mode
        </GpBtn>
      </Section>
    </template>
  </ProjectNodeLayout>
</template>
